import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Quién es dueño del borde inferior de la pantalla.
 *
 * <b>El problema que resuelve:</b> la tab bar de modo piso vive fija al pie en
 * teléfono. Una pantalla con su propia barra de acciones fija al pie choca con
 * ella — y como esa barra se dibuja encima, la tab bar queda tapada e inservible
 * mientras el header ya escondió el botón del drawer: el usuario se queda sin
 * ninguna forma de navegar.
 *
 * Mirar la URL no alcanza. La gestión de una mesa abierta, que es justo la
 * pantalla con barra fija que más usa un mesero, NO es una ruta: se monta por
 * estado dentro de /tables. Por eso la pantalla lo declara en tiempo de
 * ejecución en vez de que el layout lo adivine desde `location`.
 *
 * Al declararse, MainLayout oculta la tab bar y devuelve el botón del menú, que
 * es el mismo trato que reciben las rutas de BOTTOM_EDGE_ROUTES.
 */
interface BottomEdgeContextValue {
  /** Suma o resta un dueño. Se cuenta, no se prende: ver useOwnsBottomEdge. */
  claim: (owned: boolean) => void;
}

const BottomEdgeContext = createContext<BottomEdgeContextValue>({
  claim: () => {},
});

export const BottomEdgeProvider: React.FC<{
  claim: (owned: boolean) => void;
  children: React.ReactNode;
}> = ({ claim, children }) => {
  // El value se memoiza en MainLayout (dueño del estado); aquí sólo se propaga.
  const value = React.useMemo(() => ({ claim }), [claim]);
  return <BottomEdgeContext.Provider value={value}>{children}</BottomEdgeContext.Provider>;
};

/**
 * Declara que este componente ocupa el borde inferior mientras `owned` sea true.
 *
 * @param owned normalmente la misma condición con la que se pinta la barra
 *              (p. ej. `isMobile && !otroPanelAbierto`), para que soltar el
 *              borde y dejar de dibujar la barra pasen a la vez.
 */
export const useOwnsBottomEdge = (owned: boolean): void => {
  const { claim } = useContext(BottomEdgeContext);

  useEffect(() => {
    if (!owned) return;
    claim(true);
    // La liberación va en la limpieza para que también corra si el componente
    // se desmonta sin pasar antes por owned=false (navegar, cerrar el modal).
    return () => claim(false);
  }, [owned, claim]);
};

/**
 * Estado del lado del layout. Cuenta dueños en vez de guardar un booleano
 * porque en una transición React monta el componente entrante antes de
 * desmontar el saliente: con un booleano, el desmontaje del viejo apagaría la
 * bandera que el nuevo acaba de prender, y la tab bar reaparecería encima de
 * la barra de acciones.
 */
export const useBottomEdgeOwners = (): { owned: boolean; claim: (owned: boolean) => void } => {
  const [owners, setOwners] = useState(0);

  const claim = useCallback((owned: boolean) => {
    setOwners((count) => Math.max(0, count + (owned ? 1 : -1)));
  }, []);

  return { owned: owners > 0, claim };
};
